import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { loginOTP, requestOTP } from '../services/user';
import LogoWhite from '../assets/svg/brand/logo-white.svg';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    return `${local[0]}${'*'.repeat(Math.max(local.length - 1, 4))}@${domain}`;
};

const VerifyOTP = (props) => {
    const [error, setError] = useState(false);
    const [message, setMessage] = useState(false);
    const [code, setCode] = useState('');
    // Arranca en 60 porque el código se acaba de enviar desde /login-otp
    const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
    const email = props.location.state?.email;
    const path = new URLSearchParams(props.location.search).get('path');

    useEffect(() => {
        document.title = 'Grisino Fan - Código de verificación';
    }, []);

    useEffect(() => {
        if (!email) {
            props.history.push({ pathname: '/login-otp', search: props.location.search });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email]);

    useEffect(() => {
        if (props.auth) {
            props.history.push(path || '/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.auth]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    useEffect(() => {
        if (code.length === CODE_LENGTH) {
            submit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const submit = async (event) => {
        if (event) event.preventDefault();
        setError(false);
        setMessage(false);

        if (code.length !== CODE_LENGTH) {
            return errorHandler(0);
        }

        const result = await loginOTP(code, email);
        if (!result.token) {
            // El rate limiter responde con un string en vez de { error }
            return errorHandler(typeof result === 'string' ? result : result.error);
        }
    };

    const resend = async () => {
        if (cooldown > 0) return;
        setError(false);
        setMessage(false);
        setCode('');

        const result = await requestOTP(email);
        if (!result.success) {
            return errorHandler(typeof result === 'string' ? result : result.error);
        }
        setCooldown(RESEND_COOLDOWN);
        setMessage(`Te enviamos un nuevo código a ${maskEmail(email)}.`);
    };

    const errorHandler = (status) => {
        let text = '';
        switch (status) {
            case 0:
                text = 'Por favor, ingresá el código de 6 dígitos.';
                break;
            case 'Invalid OTP':
                text = 'El código ingresado es incorrecto.';
                break;
            case 'OTP has expired':
            case 'No OTP found for this user':
                text = 'El código venció o ya fue usado. Pedí uno nuevo.';
                break;
            case 'Disabled':
                text = 'Tu cuenta está deshabilitada. Contactanos para más información.';
                break;
            case 'User not found':
                text = 'El email ingresado no corresponde a un usuario activo.';
                break;
            case 'Failed to send OTP email':
                text = 'No pudimos enviarte el código. Inténtalo más tarde.';
                break;
            case 'Too many requests, please try again later.':
                text = 'Hiciste demasiados intentos. Esperá unos minutos y volvé a intentar.';
                break;
            default:
                text = 'Hubo un problema. Inténtalo más tarde.'
                break;
        }
        return setError(text);
    };

    return (
        <div className="signup-template">
            <div className="position-absolute top-0 start-0 p-4 p-md-5 w-100">
                <span className="ps-lg-3">
                    <img src={LogoWhite} alt="Grisino Fan" className="brand-icon-lg" />
                </span>
            </div>
            <div className="row g-0 align-items-center">
                <div className="col-lg-7 bg-black">
                    {
                        props.assets.signin_cover_web && <img src={props.assets.signin_cover_web} className="cover-fit" alt="Sign in Cover" />
                    }
                </div>
                <div className="col-lg-5 px-md-3 px-xl-5">
                    <div className="px-3 py-4 p-md-5 p-xxl-5 mx-xxl-4">
                        <div className="login-form py-2 py-md-0 mx-auto mx-lg-0">
                            <h2 className="h1 mb-3">Código de verificación</h2>
                            <p className="text-gray-700">Ingresá los 6 dígitos que enviamos a {maskEmail(email)}.</p>
                            <form className="pt-2" onSubmit={submit}>
                                <div className="mb-4 pb-md-2">
                                    <OtpInput
                                        value={code}
                                        onChange={setCode}
                                        numInputs={CODE_LENGTH}
                                        inputType="tel"
                                        shouldAutoFocus
                                        renderInput={(inputProps) => <input {...inputProps} />}
                                        containerStyle="otp-input-container"
                                        inputStyle="form-control form-control-xl otp-input"
                                    />
                                </div>
                                <div className="d-grid">
                                    {error && <p className="text-danger fw-bold">{error}</p>}
                                    {message && <p className="text-success fw-bold">{message}</p>}
                                    <button type="submit" className="btn btn-xl btn-primary">Ingresar</button>
                                </div>
                                <div className="my-4 d-flex pb-3">
                                    <button type="button" className="btn btn-link p-0 small text-gray-600 mt-1" onClick={resend} disabled={cooldown > 0}>
                                        {cooldown > 0 ? `Reenviar código en ${cooldown}s` : 'Reenviar código'}
                                    </button>
                                    <NavLink to={{ pathname: '/login-otp', search: props.location.search }} className="small text-gray-600 ms-auto mt-1">Cambiar email</NavLink>
                                </div>
                                <div className="border-top border-gray-200 pt-4">
                                    <span className="text-gray-700">¿No te llegó el código? <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} className="link-primary">Contactanos</a></span>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    auth: state.auth.isAuthenticated,
    assets: state.assets,
});

export default connect(mapStateToProps)(VerifyOTP);
